/**
 * Formidable.js — Minijuego "Formidable" (versión 2)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  obtenerJugadorOculto,
  obtenerEquipos,
  obtenerClubes,
  obtenerJugadoresParaAutocompletar,
  preguntaJugoEnClub,
  preguntaRetirado,
  preguntaAnioRetiro,
  preguntaAnioFormidable,
  preguntaJugoEnRegion,
  preguntaPosicion,
  verificarIntento,
  guardarResultado,
  checkCooldownFormidable,
} from './formidableLogic';
import './formidableStyles.css';

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const TIPOS_PREGUNTA = [
  { id: 'club',            label: 'Jugó en...' },
  { id: 'club_formidable', label: '¿En qué club fue formidable?' },
  { id: 'seleccion',       label: 'Jugó en Selección' },
  { id: 'pais',            label: 'País' },
  { id: 'retirado',        label: 'Retiro' },
  { id: 'anio',            label: 'Año formidable' },
  { id: 'posicion',        label: 'Posición' },
  { id: 'region',          label: 'Región' },
  { id: 'apodo',           label: 'Apodo' },
];

const POSICIONES = ['ARQ', 'DEF', 'VOL', 'DEL'];
const REGIONES = [
  'Argentina', 'Europa', 'Sudamérica', 'Brasil',
  'España', 'Italia', 'México', 'Ucrania', 'Inglaterra', 'Portugal',
  'Colombia', 'Paraguay', 'Uruguay', 'Perú', 'Grecia', 'Kuwait',
];
const MAX_PREGUNTAS = 20;

const STORAGE_KEY        = 'formidable_partida_activa';
const RESULTADO_KEY      = 'formidable_resultado_dia';

const Formidable = ({ session }) => {
  const [estadoJuego, setEstadoJuego] = useState('cargando');
  const [mostrarArriesgar, setMostrarArriesgar] = useState(false);
  const [jugadorOculto, setJugadorOculto]     = useState(null);
  const [historialJugador, setHistorialJugador] = useState([]);
  const [equipos, setEquipos]                 = useState([]);
  const [jugadoresBD, setJugadoresBD]         = useState([]);
  const [preguntas, setPreguntas]             = useState([]);
  const [cooldown, setCooldown]               = useState(null);
  const [rachaVictorias, setRachaVictorias]   = useState(0);

  const [tipoPregunta, setTipoPregunta]       = useState('club');
  const [clubFormidableInput, setClubFormidableInput] = useState('');
  const [clubes, setClubes]                   = useState([]);
  const [busquedaClubF, setBusquedaClubF]     = useState('');
  const [clubFSeleccionado, setClubFSeleccionado] = useState('');
  const [mostrarDropClubF, setMostrarDropClubF] = useState(false);
  const refDropClubF = React.useRef(null);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState('');
  const [busquedaEquipo, setBusquedaEquipo]   = useState('');
  const [mostrarDropClub, setMostrarDropClub] = useState(false);

  const [mostrarAnioRetiro, setMostrarAnioRetiro] = useState(false);
  const [anioRetiroInput, setAnioRetiroInput] = useState('');
  const [retiroPreguntado, setRetiroPreguntado] = useState(false);
  const [seleccionPreguntada, setSeleccionPreguntada] = useState(false);
  const [paisSeleccionado, setPaisSeleccionado] = useState('');
  const [busquedaPais, setBusquedaPais]         = useState('');
  const [mostrarDropPais, setMostrarDropPais]   = useState(false);

  const [anioInput, setAnioInput]             = useState('');
  const [regionSeleccionada, setRegionSeleccionada] = useState(REGIONES[0]);

  const [busquedaAdivinar, setBusquedaAdivinar] = useState('');
  const [jugadorSeleccionado, setJugadorSeleccionado] = useState(null);
  const [mostrarDropAdivinar, setMostrarDropAdivinar] = useState(false);

  const [apodoPreguntado, setApodoPreguntado] = useState(false);
  const [guardando, setGuardando]             = useState(false);
  const [error, setError]                     = useState('');
  const [tutorialVisto, setTutorialVisto]     = useState(() => {
    try { return localStorage.getItem('formidable_tutorial_visto') === '1'; } catch { return false; }
  });
  const cerrarTutorial = () => {
    try { localStorage.setItem('formidable_tutorial_visto', '1'); } catch {}
    setTutorialVisto(true);
  };

  const refDropAdivinar = useRef(null);
  const refDropClub     = useRef(null);
  const refDropPais     = useRef(null);

  const iniciarPartida = useCallback(async () => {
    setEstadoJuego('cargando');
    setPreguntas([]);
    setError('');
    setMostrarAnioRetiro(false);
    setRetiroPreguntado(false);
    setSeleccionPreguntada(false);
    setJugadorSeleccionado(null);
    setBusquedaAdivinar('');

    if (session?.user?.id) {
      const { puedeJugar, horasRestantes } = await checkCooldownFormidable(session.user.id);
      if (!puedeJugar) {
        setCooldown({ horasRestantes });
        setEstadoJuego('cooldown');
        return;
      }
    }
    setCooldown(null);

    const [datoJugador, listaEquipos, listaClubes, listaJugadores] = await Promise.all([
      obtenerJugadorOculto(),
      obtenerEquipos(),
      obtenerClubes(),
      obtenerJugadoresParaAutocompletar(),
    ]);

    if (!datoJugador) {
      setError('No se pudo cargar un jugador. Revisá la base de datos.');
      setEstadoJuego('error');
      return;
    }

    setJugadorOculto(datoJugador.jugador);
    setHistorialJugador(datoJugador.historial);
    setEquipos(listaEquipos);
    setClubes(listaClubes);
    setJugadoresBD(listaJugadores);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        jugador: datoJugador.jugador,
        historial: datoJugador.historial,
        equiposList: listaEquipos,
        jugadoresList: listaJugadores,
        preguntasList: [],
      }));
    } catch (_) {}
    setEstadoJuego('jugando');
  }, [session]);

  useEffect(() => {
    try {
      const guardada = sessionStorage.getItem(STORAGE_KEY);
      if (guardada) {
        const saved = JSON.parse(guardada);
        if (saved.jugador && saved.preguntasList?.length > 0) {
          setJugadorOculto(saved.jugador);
          setHistorialJugador(saved.historial || []);
          setEquipos(saved.equiposList || []);
          setJugadoresBD(saved.jugadoresList || []);
          setPreguntas(saved.preguntasList);
          setRetiroPreguntado(saved.retiroPreguntado || false);
          setSeleccionPreguntada(saved.seleccionPreguntada || false);
          setMostrarAnioRetiro(saved.mostrarAnioRetiro || false);
          setEstadoJuego('jugando');
          return;
        }
      }
    } catch (_) {}
    iniciarPartida();
  }, [iniciarPartida]);

  useEffect(() => {
    const handler = (e) => {
      if (refDropAdivinar.current && !refDropAdivinar.current.contains(e.target)) setMostrarDropAdivinar(false);
      if (refDropClub.current     && !refDropClub.current.contains(e.target))     setMostrarDropClub(false);
      if (refDropPais.current     && !refDropPais.current.contains(e.target))     setMostrarDropPais(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const registrarPregunta = (texto, respuesta, tipo = 'bool') => {
    if (preguntas.length >= MAX_PREGUNTAS) return;
    setPreguntas(prev => {
      const nuevas = [{ texto, respuesta, tipo, id: Date.now() }, ...prev];
      try {
        const guardada = sessionStorage.getItem(STORAGE_KEY);
        const base = guardada ? JSON.parse(guardada) : {};
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...base, preguntasList: nuevas }));
      } catch (_) {}
      return nuevas;
    });
  };

  const terminarJuego = async (estado) => {
    setEstadoJuego(estado);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
    // Guardar resultado del día en localStorage
    try {
      localStorage.setItem(RESULTADO_KEY, JSON.stringify({
        jugador: jugadorOculto,
        historial: historialJugador,
        preguntas,
        estado,
        fecha: new Date().toDateString(),
      }));
    } catch (_) {}
    if (!session?.user?.id) return;
    setGuardando(true);
    const resultado = await guardarResultado(session.user.id, jugadorOculto, preguntas.length, estado === 'rendido');
    if (resultado?.rachaVictorias) setRachaVictorias(resultado.rachaVictorias);
    setGuardando(false);
  };

  const handlePreguntaClub = () => {
    const team = equipos.find(e => String(e.id) === equipoSeleccionado);
    if (!team) return;
    const resultado = preguntaJugoEnClub(historialJugador, team.id);
    registrarPregunta(`Jugó en ${team.name}`, resultado ? 'Sí ✓' : 'No ✗', resultado ? 'si' : 'no');
    setEquipoSeleccionado('');
    setBusquedaEquipo('');
    setMostrarDropClub(false);
  };

  const handlePreguntaClubFormidable = () => {
    const nombre = clubFSeleccionado || busquedaClubF.trim();
    if (!nombre) return;
    if (!jugadorOculto.club_formidable) {
      registrarPregunta(`Club formidable: ${nombre}`, 'No ✗', 'no');
      setBusquedaClubF(''); setClubFSeleccionado('');
      return;
    }
    const respuesta = norm(jugadorOculto.club_formidable);
    const intento   = norm(nombre);
    const acierto   = intento.length >= 3 && (respuesta.includes(intento) || intento.includes(respuesta));
    registrarPregunta(
      `Club formidable: ${nombre}`,
      acierto ? `Sí, fue formidable en ${jugadorOculto.club_formidable} ✓` : `No ✗`,
      acierto ? 'si' : 'no'
    );
    setBusquedaClubF(''); setClubFSeleccionado('');
  };

  const handleSigueJugando = () => {
    const retirado = preguntaRetirado(jugadorOculto);
    setRetiroPreguntado(true);
    if (retirado) {
      registrarPregunta('¿Sigue jugando?', 'No, está retirado ✗', 'no');
      setMostrarAnioRetiro(true);
    } else {
      registrarPregunta('¿Sigue jugando?', 'Sí, sigue en actividad ✓', 'si');
    }
  };

  const handleAnioRetiro = () => {
    const anio = parseInt(anioRetiroInput);
    if (!anio) return;
    const resultado = preguntaAnioRetiro(jugadorOculto, anio);
    const TEXTOS = {
      exacto:      `Sí, se retiró en ${anio} ✓`,
      antes:       `Se retiró ANTES de ${anio} ⬆️`,
      después:     `Se retiró DESPUÉS de ${anio} ⬇️`,
      no_retirado: 'No está retirado',
    };
    registrarPregunta(`Se retiró en ${anio}`, TEXTOS[resultado], resultado === 'exacto' ? 'si' : 'pista');
    setAnioRetiroInput('');
  };

  // ── Handler apodo ────────────────────────────────────────────────────────
  const handlePreguntaApodo = (respuesta) => {
    const tieneApodo = !!jugadorOculto?.apodo;
    const tipoApodo  = jugadorOculto?.tipo_apodo || null;
    const etiquetas  = { objeto: 'un objeto', animal: 'un animal', nombre: 'un nombre / fantasía', fisico: 'una descripción física' };
    let resultado, textoPregunta, textoRespuesta;

    if (respuesta === 'tiene') {
      resultado     = tieneApodo ? 'si' : 'no';
      textoPregunta = '¿Tiene apodo?';
      textoRespuesta = tieneApodo ? 'Sí, tiene apodo ✓' : 'No tiene apodo ✗';
      setApodoPreguntado(true);
    } else {
      resultado      = tipoApodo === respuesta ? 'si' : 'no';
      textoPregunta  = `¿El apodo es ${etiquetas[respuesta]}?`;
      textoRespuesta = tipoApodo === respuesta ? `Sí, es ${etiquetas[respuesta]} ✓` : `No es ${etiquetas[respuesta]} ✗`;
    }
    registrarPregunta(textoPregunta, textoRespuesta, resultado);
  };

  const handlePreguntaAnio = () => {
    const anio = parseInt(anioInput);
    if (!anio) return;
    const resultado = preguntaAnioFormidable(jugadorOculto, anio);
    const TEXTOS = {
      exacto:  `¡Exacto! ${anio} fue su año formidable `,
      antes:   `El año formidable fue ANTES de ${anio}⬇️ `,
      después: `El año formidable fue DESPUÉS de ${anio} ⬆️`,
    };
    registrarPregunta(`Año formidable: ${anio}`, TEXTOS[resultado], resultado === 'exacto' ? 'si' : 'pista');
    setAnioInput('');
  };

  const handlePreguntaPosicion = (sigla) => {
    const resultado = preguntaPosicion(jugadorOculto, sigla);
    const labels = { ARQ: 'Arquero', DEF: 'Defensor', VOL: 'Volante', DEL: 'Delantero' };
    registrarPregunta(`Posición: ${labels[sigla] || sigla}`, resultado ? 'Sí ✓' : 'No ✗', resultado ? 'si' : 'no');
  };

  const handlePreguntaSeleccion = () => {
    const jugo = jugadorOculto.jugo_en_seleccion === true;
    registrarPregunta(
      '¿Jugó en selección nacional?',
      jugo ? 'Sí, jugó en selección ✓' : 'No jugó en selección ✗',
      jugo ? 'si' : 'no'
    );
    setSeleccionPreguntada(true);
  };

  const handlePreguntaPais = () => {
    if (!paisSeleccionado) return;
    const paisJugador = jugadorOculto.nacionalidad || '';
    const coincide = norm(paisJugador) === norm(paisSeleccionado);
    registrarPregunta(
      `¿Es de ${paisSeleccionado}?`,
      coincide ? `Sí, es de ${paisJugador} ✓` : `No es de ${paisSeleccionado} ✗`,
      coincide ? 'si' : 'no'
    );
    setPaisSeleccionado('');
    setBusquedaPais('');
  };

  const handlePreguntaRegion = () => {
    const periodos = preguntaJugoEnRegion(historialJugador, regionSeleccionada);
    if (!periodos || periodos.length === 0) {
      registrarPregunta(`¿Jugó en ${regionSeleccionada}?`, `No jugó en ${regionSeleccionada} ✗`, 'no');
    } else {
      const años = periodos.map(h => ({
        desde: h.year_from || 0,
        hasta: h.year_to || new Date().getFullYear(),
      }));
      años.sort((a, b) => a.desde - b.desde);
      const rangos = [];
      años.forEach(({ desde, hasta }) => {
        if (rangos.length && desde <= rangos[rangos.length - 1].hasta + 1) {
          rangos[rangos.length - 1].hasta = Math.max(rangos[rangos.length - 1].hasta, hasta);
        } else {
          rangos.push({ desde, hasta });
        }
      });
      const texto = rangos.map(r => r.desde === r.hasta ? `${r.desde}` : `${r.desde}–${r.hasta}`).join(', ');
      registrarPregunta(`¿Jugó en ${regionSeleccionada}?`, `Sí ✓ — ${texto}`, 'si');
    }
  };

  const handleAdivinar = () => {
    if (!jugadorSeleccionado) return;
    const correcto = verificarIntento(jugadorOculto, jugadorSeleccionado.id);
    if (correcto) {
      registrarPregunta(`Intento: "${jugadorSeleccionado.apellido}, ${jugadorSeleccionado.nombre}"`, '¡Correcto! 🎉', 'si');
      terminarJuego('ganado');
    } else {
      registrarPregunta(`Intento: "${jugadorSeleccionado.apellido}, ${jugadorSeleccionado.nombre}"`, 'Incorrecto — ¡perdiste! ✗', 'no');
      terminarJuego('rendido');
    }
  };

  const PAISES_LISTA = ['Argentina', 'Brasil', 'Colombia', 'España', 'Francia', 'Inglaterra', 'Italia', 'México', 'Paraguay', 'Perú', 'Portugal', 'Rumania', 'Ucrania', 'Uruguay'];
  const paisesFiltrados = PAISES_LISTA.filter(p => norm(p).includes(norm(busquedaPais))).slice(0, 8);
  const equiposFiltrados = equipos.filter(e =>
    norm(e.name).includes(norm(busquedaEquipo)) ||
    norm(e.country?.name || '').includes(norm(busquedaEquipo))
  ).slice(0, 8);
  const jugadoresFiltrados = jugadoresBD.filter(j =>
    norm(`${j.apellido} ${j.nombre}`).includes(norm(busquedaAdivinar)) ||
    norm(`${j.nombre} ${j.apellido}`).includes(norm(busquedaAdivinar))
  ).slice(0, 8);

  if (estadoJuego === 'cargando') return (
    <div className="formidable-wrapper">
      <p style={{ color: '#cbd5e1', textAlign: 'center', marginTop: '60px' }}>Buscando jugador...</p>
    </div>
  );

  if (estadoJuego === 'cooldown') {
    let resultadoDia = null;
    try {
      const saved = localStorage.getItem(RESULTADO_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.fecha === new Date().toDateString()) resultadoDia = parsed;
      }
    } catch (_) {}

    return (
      <div className="formidable-wrapper">
        <div style={{ textAlign: 'center', padding: '12px 20px', background: 'rgba(245,158,11,0.08)', border: '1px solid #f59e0b33', borderRadius: '12px', marginBottom: '14px' }}>
          <p style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '1rem', margin: '0 0 2px' }}>Ya jugaste hoy</p>
          <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>
            Volvé en <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>{cooldown?.horasRestantes}hs</span> para el próximo jugador.
          </p>
        </div>

        {resultadoDia && (() => {
          const { jugador, historial: hist, preguntas: pregs, estado: est } = resultadoDia;
          const gano = est === 'ganado';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ textAlign: 'center', padding: '12px', background: gano ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${gano ? '#22c55e44' : '#ef444433'}`, borderRadius: '10px' }}>
                <p style={{ color: gano ? '#22c55e' : '#ef4444', fontWeight: 'bold', fontSize: '1rem', margin: '0 0 2px' }}>
                  {gano ? 'Lo adivinaste!' : 'No lo adivinaste'}
                </p>
                <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0 }}>{pregs?.length || 0} preguntas usadas</p>
              </div>
              <div className="formidable-panel" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                {jugador?.foto_url && (
                  <img src={jugador.foto_url} alt={jugador?.apellido}
                    style={{ width: '68px', height: '68px', objectFit: 'cover', borderRadius: '50%', border: '2px solid #a855f7', flexShrink: 0 }} />
                )}
                <div>
                  <p style={{ color: '#a855f7', fontWeight: 900, fontSize: '1.1rem', margin: '0 0 2px' }}>
                    {jugador?.apellido}, {jugador?.nombre}
                  </p>
                  {jugador?.formidable_year && (
                    <p style={{ color: '#f59e0b', fontSize: '0.82rem', margin: '0 0 2px' }}>
                      Año formidable: <strong>{jugador.formidable_year}</strong>
                      {jugador.club_formidable && ` en ${jugador.club_formidable}`}
                    </p>
                  )}
                  <p style={{ color: '#64748b', fontSize: '0.75rem', margin: 0 }}>
                    {jugador?.posicion?.sigla} · {jugador?.nacionalidad}
                  </p>
                </div>
              </div>
              {pregs?.length > 0 && (
                <div className="formidable-panel">
                  <p className="formidable-panel-titulo">Historial — {pregs.length} pregunta{pregs.length !== 1 ? 's' : ''}</p>
                  <div className="formidable-historial">
                    {pregs.map((p, i) => <EntradaHistorial key={i} entrada={p} />)}
                  </div>
                </div>
              )}
              <div style={{ textAlign: 'center', background: '#0a0f19', border: '1px solid #f59e0b33', borderRadius: '10px', padding: '12px' }}>
                <p style={{ color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 4px' }}>Racha de victorias</p>
                <p style={{ color: '#f59e0b', fontSize: '1.8rem', fontWeight: 'bold', margin: 0 }}>🔥 {rachaVictorias}</p>
              </div>
            </div>
          );
        })()}

        {!resultadoDia && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ background: '#0a0f19', border: '1px solid #f59e0b33', borderRadius: '12px', padding: '16px 24px', display: 'inline-block' }}>
              <p style={{ color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 4px' }}>Racha de victorias</p>
              <p style={{ color: '#f59e0b', fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>🔥 {rachaVictorias}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (estadoJuego === 'error') return (
    <div className="formidable-wrapper">
      <p style={{ color: '#ff4444', textAlign: 'center' }}>{error}</p>
      <button className="formidable-btn" onClick={iniciarPartida}>Reintentar</button>
    </div>
  );

  if (estadoJuego === 'ganado' || estadoJuego === 'rendido') {
    const gano = estadoJuego === 'ganado';
    const BANDERAS_MAP = {'Argentina':'🇦🇷','Brasil':'🇧🇷','Colombia':'🇨🇴','España':'🇪🇸','Francia':'🇫🇷','Inglaterra':'🏴󠁧󠁢󠁥󠁮󠁧󠁿','Italia':'🇮🇹','México':'🇲🇽','Paraguay':'🇵🇾','Perú':'🇵🇪','Portugal':'🇵🇹','Rumania':'🇷🇴','Ucrania':'🇺🇦','Uruguay':'🇺🇾'};
    return (
      <div className="formidable-wrapper">
        <div style={{ textAlign: 'center', padding: '18px', background: gano ? 'rgba(34,197,94,0.08)' : 'rgba(255,68,68,0.08)', border: `1px solid ${gano ? '#22c55e44' : '#ff444433'}`, borderRadius: '12px' }}>
          <span style={{ fontSize: '2rem' }}>{gano ? '' : ''}</span>
          <h2 style={{ color: gano ? '#22c55e' : '#ff4444', margin: '6px 0 0', fontSize: '1.3rem', fontWeight: 'bold' }}>
            {gano ? '¡Lo adivinaste!' : 'Perdiste'}
          </h2>
        </div>

        <div style={{ background: '#080d14', border: '1px solid #1e293b', borderRadius: '16px', padding: '28px', display: 'flex', gap: '32px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', minWidth: '160px' }}>
            {jugadorOculto.foto_url ? (
              <img src={jugadorOculto.foto_url} alt={`${jugadorOculto.nombre} ${jugadorOculto.apellido}`}
                style={{ width: '100px', height: '100px', objectFit: 'cover', objectPosition: 'center top', borderRadius: '12px', border: '2px solid #a855f744', boxShadow: '0 0 24px rgba(168,85,247,0.25)' }} />
            ) : (
              <div style={{ width: '160px', height: '160px', borderRadius: '12px', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>👤</div>
            )}
            <div>
              <p style={{ color: '#a855f7', fontWeight: '900', fontSize: '1.3rem', letterSpacing: '0.5px', margin: '0 0 4px', textAlign: 'center', textShadow: '0 0 14px #a855f766' }}>
                {jugadorOculto.seleccion_emoji && <span style={{ marginRight: '6px' }}>{jugadorOculto.seleccion_emoji}</span>}
                {jugadorOculto.nombre} {jugadorOculto.apellido}
              </p>
              <p style={{ color: '#cbd5e1', fontSize: '0.85rem', margin: '0 0 2px', textAlign: 'center' }}>
                Año formidable: <strong style={{ color: '#f59e0b' }}>{jugadorOculto.formidable_year}</strong>
                {jugadorOculto.formidable_year_alt && <> / <strong style={{ color: '#f59e0b' }}>{jugadorOculto.formidable_year_alt}</strong></>}
              </p>
              {jugadorOculto.club_formidable && (
                <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: 0, textAlign: 'center' }}>
                  🏟️ Formidable en <strong style={{ color: '#e2e8f0' }}>{jugadorOculto.club_formidable}</strong>
                </p>
              )}
              <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: 0, textAlign: 'center' }}>
                Posición: <strong style={{ color: '#a855f7' }}>{jugadorOculto.posicion?.sigla}</strong>
              </p>
            </div>
            {jugadorOculto.youtube_url && (
              <a href={jugadorOculto.youtube_url} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ff000022', border: '1px solid #ff000055', color: '#ff4444', borderRadius: '8px', padding: '7px 14px', fontSize: '0.85rem', fontWeight: 'bold', textDecoration: 'none', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.background = '#ff000044'}
                onMouseLeave={e => e.currentTarget.style.background = '#ff000022'}>
                ▶ Ver highlights
              </a>
            )}
          </div>

          <div style={{ flex: 1, minWidth: '260px' }}>
            <p style={{ color: '#697585', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 10px' }}>Carrera</p>
            <div style={{ border: '1px solid #1e293b', borderRadius: '10px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ background: '#0f172a' }}>
                    {['Club', 'País', 'Año'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', color: '#94a3b8', fontWeight: '600', textAlign: h === 'Año' ? 'center' : 'left', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #1e293b' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historialJugador.map((h, i) => {
                    const pais = h.team?.country?.name || '';
                    const bandera = BANDERAS_MAP[pais] || '';
                    const esFormidable = jugadorOculto.formidable_year >= (h.year_from||0) && jugadorOculto.formidable_year <= (h.year_to||9999);
                    return (
                      <tr key={i} style={{ background: esFormidable ? 'rgba(248,197,0,0.07)' : i % 2 === 0 ? '#080d14' : '#0a0f19', transition: '0.15s' }}>
                        <td style={{ padding: '9px 12px', color: esFormidable ? '#f59e0b' : '#e2e8f0', fontWeight: esFormidable ? '700' : '400', borderBottom: '1px solid #0f172a' }}>
                          {h.team?.name || '—'}
                          {esFormidable && <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: '#f59e0b', background: '#f59e0b18', border: '1px solid #f59e0b44', borderRadius: '4px', padding: '1px 5px' }}>★</span>}
                        </td>
                        <td style={{ padding: '9px 12px', color: '#cbd5e1', borderBottom: '1px solid #0f172a', whiteSpace: 'nowrap' }}>{pais || '—'}</td>
                        <td style={{ padding: '9px 12px', color: '#94a3b8', textAlign: 'center', borderBottom: '1px solid #0f172a', whiteSpace: 'nowrap' }}>
                          {h.year_from === h.year_to || !h.year_to ? h.year_from : `${h.year_from}–${h.year_to}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, background: '#0a0f19', border: '1px solid #a855f733', borderRadius: '10px', padding: '12px 18px', color: '#a855f7', fontWeight: 'bold', fontSize: '0.9rem', textAlign: 'center' }}>
            Formidable — {jugadorOculto.nombre} {jugadorOculto.apellido} — {preguntas.length} pregunta{preguntas.length !== 1 ? 's' : ''}
          </div>
          <div style={{ background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '10px', padding: '12px 18px', fontSize: '0.9rem', color: '#cbd5e1', textAlign: 'center', whiteSpace: 'nowrap' }}>
            Mañana habrá un nuevo jugador
          </div>
        </div>

        {guardando && <p style={{ color: '#cbd5e1', fontSize: '0.85rem', textAlign: 'center', margin: 0 }}>Guardando resultado...</p>}

        {preguntas.length > 0 && (
          <div className="formidable-panel">
            <p className="formidable-panel-titulo">Historial de la partida</p>
            <div className="formidable-historial">
              {[...preguntas].reverse().map(p => <EntradaHistorial key={p.id} entrada={p} />)}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="formidable-wrapper">

      {!tutorialVisto && (
        <div style={{ position:'fixed', inset:0, background:'rgba(2,8,18,0.93)', backdropFilter:'blur(6px)', zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div style={{ background:'#0f172a', border:'1px solid #a855f744', borderRadius:'20px', padding:'36px 32px', maxWidth:'440px', width:'100%', display:'flex', flexDirection:'column', gap:'18px', textAlign:'center' }}>
            <span style={{ fontSize:'3rem' }}>🧠</span>
            <h2 style={{ color:'#a855f7', margin:0, fontSize:'1.5rem', fontWeight:900, letterSpacing:'2px', textTransform:'uppercase' }}>FORMIDABLE</h2>
            <p style={{ color:'#e2e8f0', fontSize:'1rem', lineHeight:1.7, margin:0 }}>
              Tenés <strong style={{ color:'#a855f7' }}>20 preguntas</strong> para adivinar al <strong style={{ color:'#f59e0b' }}>jugador enigmático</strong> de hoy.
            </p>
            <p style={{ color:'#94a3b8', fontSize:'0.92rem', lineHeight:1.6, margin:0 }}>
              Podés hacer preguntas de todo tipo con el panel en pantalla — clubes, selección, país, edad, posición y más. ¡Animate!
            </p>
            <div style={{ display:'flex', gap:'8px', justifyContent:'center', flexWrap:'wrap' }}>
              {['Clubes','País','Año','Posición','Adivinar'].map(chip => (
                <span key={chip} style={{ background:'#a855f715', border:'1px solid #a855f733', borderRadius:'20px', padding:'4px 14px', color:'#a855f7', fontSize:'0.82rem', fontWeight:'bold' }}>{chip}</span>
              ))}
            </div>
            <button onClick={cerrarTutorial} style={{ padding:'14px', background:'#a855f7', color:'#fff', border:'none', borderRadius:'12px', fontWeight:900, fontSize:'1rem', cursor:'pointer', letterSpacing:'1px' }}>
              ¡Jugar!
            </button>
          </div>
        </div>
      )}

      <div className="formidable-header">
        <div>
          <h1 className="formidable-titulo">FORMIDABLE</h1>
          <p className="formidable-descripcion">Adiviná al jugador que jugó en el fútbol argentino y tuvo su año formidable.</p>
        </div>
        <div className="formidable-contador">
          <p className="formidable-contador-numero" style={{ color: preguntas.length >= MAX_PREGUNTAS - 2 ? '#ef4444' : undefined }}>{preguntas.length}</p>
          <p className="formidable-contador-label">/ {MAX_PREGUNTAS}</p>
        </div>
      </div>

      {preguntas.length >= MAX_PREGUNTAS - 2 && preguntas.length < MAX_PREGUNTAS && (
        <div style={{ background: '#ef444415', border: '1px solid #ef444444', borderRadius: '8px', padding: '8px 14px', marginBottom: '8px', textAlign: 'center' }}>
          <p style={{ color: '#ef4444', fontSize: '0.9rem', margin: 0 }}>
            ⚠️ Te quedan <strong>{MAX_PREGUNTAS - preguntas.length}</strong> pregunta{MAX_PREGUNTAS - preguntas.length !== 1 ? 's' : ''} — ¡adiviná o rendite!
          </p>
        </div>
      )}

      {preguntas.length >= MAX_PREGUNTAS && !mostrarArriesgar && (
        <div style={{ background: '#0a0f19', border: '1px solid #f59e0b44', borderRadius: '12px', padding: '20px', textAlign: 'center', marginBottom: '16px' }}>
          <p style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '1rem', margin: '0 0 4px' }}>Límite de preguntas alcanzado</p>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 16px' }}>Usaste las {MAX_PREGUNTAS} preguntas. ¿Arriesgás o te rendís?</p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="formidable-btn" onClick={() => setMostrarArriesgar(true)}>Arriesgar</button>
            <button className="formidable-btn-sec" onClick={() => terminarJuego('rendido')}>Rendirse</button>
          </div>
        </div>
      )}

      {preguntas.length < MAX_PREGUNTAS && !mostrarArriesgar && (
        <div className="formidable-panel">
          <p className="formidable-panel-titulo">Hacer una pregunta</p>

          <div className="formidable-tabs">
            {TIPOS_PREGUNTA.map(t => (
              <button key={t.id} className={`formidable-tab ${tipoPregunta === t.id ? 'activo' : ''}`} onClick={() => setTipoPregunta(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {tipoPregunta === 'club_formidable' && (
            <div className="formidable-input-zona">
              <div ref={refDropClubF} style={{ flex: 1, position: 'relative' }}>
                <input className="formidable-input" placeholder="¿En qué club fue formidable?"
                  value={clubFSeleccionado ? (clubes.find(c => c.nombre === clubFSeleccionado)?.nombre || busquedaClubF) : busquedaClubF}
                  onChange={e => { setBusquedaClubF(e.target.value); setClubFSeleccionado(''); setMostrarDropClubF(true); }}
                  onFocus={() => setMostrarDropClubF(true)} />
                {mostrarDropClubF && busquedaClubF && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:100, background:'#0f172a', border:'1px solid #a855f744', borderRadius:'8px', marginTop:'4px', overflow:'hidden', maxHeight:'200px', overflowY:'auto' }}>
                    {clubes.filter(c => norm(c.nombre).includes(norm(busquedaClubF))).slice(0,10).map(c => (
                      <div key={c.id} style={{ padding:'10px 14px', cursor:'pointer', fontSize:'0.9rem', color:'#e2e8f0', borderBottom:'1px solid #1e293b' }}
                        onMouseDown={() => { setClubFSeleccionado(c.nombre); setBusquedaClubF(c.nombre); setMostrarDropClubF(false); }}>
                        {c.nombre}
                      </div>
                    ))}
                    {clubes.filter(c => norm(c.nombre).includes(norm(busquedaClubF))).length === 0 && (
                      <div style={{ padding:'10px 14px', color:'#475569', fontSize:'0.85rem' }}>Sin resultados</div>
                    )}
                  </div>
                )}
              </div>
              <button className="formidable-btn" onClick={handlePreguntaClubFormidable} disabled={!busquedaClubF.trim()}>Preguntar</button>
            </div>
          )}

          {tipoPregunta === 'club' && (
            <div className="formidable-input-zona">
              <div ref={refDropClub} style={{ flex: 1, position: 'relative' }}>
                <input className="formidable-input" placeholder="Escribí el nombre del club..."
                  value={equipoSeleccionado ? (equipos.find(e => String(e.id) === equipoSeleccionado)?.name || busquedaEquipo) : busquedaEquipo}
                  onChange={e => { setBusquedaEquipo(e.target.value); setEquipoSeleccionado(''); setMostrarDropClub(true); }}
                  onFocus={() => setMostrarDropClub(true)} />
                {mostrarDropClub && busquedaEquipo && equiposFiltrados.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#0f172a', border: '1px solid #a855f744', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' }}>
                    {equiposFiltrados.map(e => (
                      <div key={e.id} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '0.9rem', color: '#e2e8f0', borderBottom: '1px solid #1e293b' }}
                        onMouseDown={() => { setEquipoSeleccionado(String(e.id)); setBusquedaEquipo(e.name); setMostrarDropClub(false); }}>
                        {e.name}
                        <span style={{ color: '#cbd5e1', fontSize: '0.9rem', marginLeft: '8px' }}>{e.country?.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button className="formidable-btn" onClick={handlePreguntaClub} disabled={!equipoSeleccionado}>Preguntar</button>
            </div>
          )}

          {tipoPregunta === 'seleccion' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {!seleccionPreguntada && (
                <div className="formidable-input-zona">
                  <button className="formidable-btn" style={{ flex: 1 }} onClick={handlePreguntaSeleccion}>¿Jugó en selección nacional?</button>
                </div>
              )}
              {seleccionPreguntada && (
                <p style={{ color: '#22c55e', fontSize: '0.9rem' }}>
                  {jugadorOculto?.jugo_en_seleccion ? 'Sí jugó en selección — ya respondida.' : 'No jugó en selección — ya respondida.'}
                </p>
              )}
            </div>
          )}

          {tipoPregunta === 'pais' && (
            <div className="formidable-input-zona">
              <div ref={refDropPais} style={{ flex: 1, position: 'relative' }}>
                <input className="formidable-input" placeholder="Escribí el país..."
                  value={paisSeleccionado || busquedaPais}
                  onChange={e => { setBusquedaPais(e.target.value); setPaisSeleccionado(''); setMostrarDropPais(true); }}
                  onFocus={() => setMostrarDropPais(true)} autoFocus />
                {mostrarDropPais && busquedaPais && paisesFiltrados.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#0f172a', border: '1px solid #a855f744', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' }}>
                    {paisesFiltrados.map(p => (
                      <div key={p} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '0.9rem', color: '#e2e8f0', borderBottom: '1px solid #1e293b' }}
                        onMouseDown={() => { setPaisSeleccionado(p); setBusquedaPais(p); setMostrarDropPais(false); }}>{p}</div>
                    ))}
                  </div>
                )}
              </div>
              <button className="formidable-btn" onClick={handlePreguntaPais} disabled={!paisSeleccionado}>Preguntar</button>
            </div>
          )}

          {tipoPregunta === 'retirado' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {!retiroPreguntado && (
                <div className="formidable-input-zona">
                  <button className="formidable-btn" style={{ flex: 1 }} onClick={handleSigueJugando}>¿Sigue jugando?</button>
                </div>
              )}
              {retiroPreguntado && !mostrarAnioRetiro && (
                <p style={{ color: '#22c55e', fontSize: '0.9rem' }}>Sigue en actividad — ya respondida.</p>
              )}
              {mostrarAnioRetiro && (
                <div className="formidable-input-zona">
                  <span style={{ color: '#cbd5e1', fontSize: '0.9rem', whiteSpace: 'nowrap', alignSelf: 'center' }}>Se retiró en...</span>
                  <input type="number" className="formidable-input" placeholder="Año (ej: 2018)" value={anioRetiroInput}
                    onChange={e => setAnioRetiroInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAnioRetiro()} min="1950" max="2030" />
                  <button className="formidable-btn" onClick={handleAnioRetiro} disabled={!anioRetiroInput}>Preguntar</button>
                </div>
              )}
            </div>
          )}

          {tipoPregunta === 'anio' && (
            <div className="formidable-input-zona">
              <input type="number" className="formidable-input" placeholder="Ingresá un año (ej: 2012)" value={anioInput}
                onChange={e => setAnioInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handlePreguntaAnio()} min="1950" max="2030" />
              <button className="formidable-btn" onClick={handlePreguntaAnio} disabled={!anioInput}>Preguntar</button>
            </div>
          )}

          {tipoPregunta === 'posicion' && (
            <div className="formidable-input-zona">
              {POSICIONES.map(pos => (
                <button key={pos} className="formidable-btn" style={{ flex: 1, background: '#1e293b', color: '#cbd5e1' }} onClick={() => handlePreguntaPosicion(pos)}>{pos}</button>
              ))}
            </div>
          )}

          {tipoPregunta === 'region' && (
            <div className="formidable-input-zona">
              <select className="formidable-select" value={regionSeleccionada} onChange={e => setRegionSeleccionada(e.target.value)} style={{ flex: 1 }}>
                {REGIONES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <button className="formidable-btn" onClick={handlePreguntaRegion}>Preguntar</button>
            </div>
          )}

          {/* ── Apodo ── */}
          {tipoPregunta === 'apodo' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div className="formidable-input-zona">
                <button className="formidable-btn" style={{ flex: 1 }} onClick={() => handlePreguntaApodo('tiene')}>
                  ¿Tiene apodo?
                </button>
              </div>
              {apodoPreguntado && jugadorOculto?.apodo && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', width: '100%', margin: '0 0 4px' }}>¿El apodo es...?</p>
                  {[
                    { id: 'objeto', label: 'Un objeto' },
                    { id: 'animal', label: 'Un animal' },
                    { id: 'nombre', label: 'Un nombre / fantasía' },
                    { id: 'fisico', label: 'Descripción física' },
                  ].map(({ id, label }) => (
                    <button key={id} className="formidable-btn"
                      style={{ flex: '1 1 40%', background: '#1e293b', color: '#cbd5e1', fontSize: '0.82rem' }}
                      onClick={() => handlePreguntaApodo(id)}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {preguntas.length > 0 && (
        <div className="formidable-panel">
          <p className="formidable-panel-titulo">Historial — {preguntas.length} pregunta{preguntas.length !== 1 ? 's' : ''}</p>
          <div className="formidable-historial">
            {preguntas.map(p => <EntradaHistorial key={p.id} entrada={p} />)}
          </div>
        </div>
      )}

      {(preguntas.length < MAX_PREGUNTAS || mostrarArriesgar) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {mostrarArriesgar && (
            <p style={{ color: '#f59e0b', fontSize: '0.82rem', textAlign: 'center', margin: '0 0 4px', fontWeight: 'bold' }}>
              Última oportunidad — si errás, perdés
            </p>
          )}
          <div className="formidable-adivinar">
            <div ref={refDropAdivinar} style={{ flex: 1, position: 'relative' }}>
              <input className="formidable-input" placeholder="Escribí el apellido para adivinar..."
                value={jugadorSeleccionado ? `${jugadorSeleccionado.apellido}, ${jugadorSeleccionado.nombre}` : busquedaAdivinar}
                onChange={e => { setBusquedaAdivinar(e.target.value); setJugadorSeleccionado(null); setMostrarDropAdivinar(true); }}
                onFocus={() => setMostrarDropAdivinar(true)} />
              {mostrarDropAdivinar && busquedaAdivinar.length >= 3 && jugadoresFiltrados.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#0f172a', border: '1px solid #a855f744', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' }}>
                  {jugadoresFiltrados.map(j => (
                    <div key={j.id} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '0.9rem', color: '#e2e8f0', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between' }}
                      onMouseDown={() => { setJugadorSeleccionado(j); setBusquedaAdivinar(''); setMostrarDropAdivinar(false); }}>
                      <span>{j.apellido}, {j.nombre}</span>
                      <span style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>{j.posicion?.sigla}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button className="formidable-btn" onClick={handleAdivinar} disabled={!jugadorSeleccionado}>Adivinar</button>
          </div>
          {!mostrarArriesgar && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="formidable-btn-sec" onClick={() => { if (window.confirm('¿Seguro que querés rendirte y ver la respuesta?')) terminarJuego('rendido'); }}>
              Rendirse
            </button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

const ICONOS = { si: '✅', no: '❌', pista: '💡' };

const EntradaHistorial = ({ entrada }) => (
  <div className={`formidable-entrada formidable-entrada-${entrada.tipo}`}>
    <span className="formidable-entrada-icono">{ICONOS[entrada.tipo] || '❓'}</span>
    <span className="formidable-entrada-texto">{entrada.texto}</span>
    <span className={`formidable-entrada-respuesta resp-${entrada.tipo}`}>{entrada.respuesta}</span>
  </div>
);

export default Formidable;